// src/services/banks/bankService.js
import { createCrudService } from '../api/baseService';
import easyCheckApi, { easyCheckRequest } from '../api/config';
import { useState, useCallback } from 'react';

const bankBaseCrud = createCrudService('banks');

export const bankService = {
  ...bankBaseCrud,

  // Bank-specific endpoints
  getBanksByCompany: async () => {
    return easyCheckRequest(() => 
      easyCheckApi.get('/banks/company')
    );
  },

  validateRoutingNumber: async (routingNumber) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/banks/validate-routing', { routingNumber })
    );
  },

  getBankFormats: async (bankId) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/banks/${bankId}/formats`)
    );
  }
};

export const easyCheckBankService = {
  getAllBanks: async (pageNumber = 1, pageSize = 10) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/banks?pageNumber=${pageNumber}&pageSize=${pageSize}`)
    );
  },

  getBankById: async (id) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/banks/${id}`)
    );
  },

  createBank: async (bankData) => {
    console.log('🏦 Creating bank account:', bankData);
    const result = await easyCheckRequest(() => 
      easyCheckApi.post('/banks', bankData)
    );
    console.log('🏦 Create bank result:', result);
    return result;
  },

  updateBank: async (id, bankData) => {
    return easyCheckRequest(() => 
      easyCheckApi.put(`/banks/${id}`, bankData)
    );
  },

  deleteBank: async (id) => {
    return easyCheckRequest(() => 
      easyCheckApi.delete(`/banks/${id}`)
    );
  },

  // Additional utility methods
  getBanksByCurrency: async (currency) => {
    return easyCheckRequest(() => 
      easyCheckApi.get(`/banks?currency=${currency}`)
    );
  },

  validateAccountNumber: async (accountNumber) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/banks/validate-account', { accountNumber })
    );
  },

  // Import functionality
  importBanks: async (formData) => {
    return easyCheckRequest(() => 
      easyCheckApi.post('/banks/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    );
  },

  downloadTemplate: async () => {
    return easyCheckRequest(() => 
      easyCheckApi.get('/banks/template', {
        responseType: 'blob',
      })
    );
  }
};

// Custom hook for using bank service with local state management
export const useBankService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const executeRequest = useCallback(async (requestFn) => {
    setLoading(true);
    setError(null);
    try {
      const result = await requestFn();
      return result;
    } catch (err) {
      setError(err.message || 'An error occurred');
      return { success: false, message: err.message || 'An error occurred' };
    } finally {
      setLoading(false);
    }
  }, []);

  const getAllBanks = useCallback((pageNumber, pageSize) => executeRequest(() => easyCheckBankService.getAllBanks(pageNumber, pageSize)), [executeRequest]);
  const getBankById = useCallback((id) => executeRequest(() => easyCheckBankService.getBankById(id)), [executeRequest]);
  const createBank = useCallback((bankData) => executeRequest(() => easyCheckBankService.createBank(bankData)), [executeRequest]);
  const updateBank = useCallback((id, bankData) => executeRequest(() => easyCheckBankService.updateBank(id, bankData)), [executeRequest]);
  const deleteBank = useCallback((id) => executeRequest(() => easyCheckBankService.deleteBank(id)), [executeRequest]);
  const getBanksByCurrency = useCallback((currency) => executeRequest(() => easyCheckBankService.getBanksByCurrency(currency)), [executeRequest]);
  const validateAccountNumber = useCallback((accountNumber) => executeRequest(() => easyCheckBankService.validateAccountNumber(accountNumber)), [executeRequest]);
  const importBanks = useCallback((formData) => executeRequest(() => easyCheckBankService.importBanks(formData)), [executeRequest]);
  const downloadTemplate = useCallback(() => executeRequest(() => easyCheckBankService.downloadTemplate()), [executeRequest]);

  return {
    loading,
    error,
    getAllBanks,
    getBankById,
    createBank,
    updateBank,
    deleteBank,
    getBanksByCurrency,
    validateAccountNumber,
    importBanks,
    downloadTemplate
  };
}; 